// src/components/etapaproductiva/RegistrarEProductiva.jsx
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";



// ===== Helpers de fecha =====
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return { y, m, d };
};
const fmtISO = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Suma `months` meses y ajusta al último día del mes si hace falta */
const addMonthsClamped = (isoDate, months) => {
  const p = parseISO(isoDate);
  if (!p) return "";
  const base = new Date(p.y, p.m - 1 + months, 1);
  const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const day = Math.min(p.d, lastDay);
  return fmtISO(base.getFullYear(), base.getMonth() + 1, day);
};

// Para enviar la fecha como 00:00 UTC
const toISODateUTC = (v) =>
  v ? new Date(`${v}T00:00:00.000Z`).toISOString() : undefined;

const RegistrarEProductiva = () => {
  const navigate = useNavigate();
  const { aprendizId, matriculaId, epracticaId } = useParams();


  const matchBulk = useMatch("/etapaproductiva/registromasivo/:matriculaId");
  const isBulk = Boolean(matchBulk);

  // =======================
  // ✅ SOLO INPUTS (individual)
  // =======================
  const [aprendizInfo, setAprendizInfo] = useState(null); // {nombres..., documentoAprendiz}
  const [matriculasOptions, setMatriculasOptions] = useState([]); // opciones por doc
  const [loadingMatriculas, setLoadingMatriculas] = useState(false);

  const [documentoAprendizUI, setDocumentoAprendizUI] = useState("");
  const [matriculaUI, setMatriculaUI] = useState("");

  // 🔹 Estados de aprendiz y matrícula (payload)
  const [aprendizObjectId, setAprendizObjectId] = useState("");
  const [documentoAprendiz, setDocumentoAprendiz] = useState("");
  const [matriculaObjectId, setMatriculaObjectId] = useState("");
  const [codMatricula, setCodMatricula] = useState("");

  // 🔹 Instructores y jefes
  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [documentoInstructor, setDocumentoInstructor] = useState("");

  const [enteObjectId, setEnteObjectId] = useState("");
  const [nitEnteconformador, setNitEnteconformador] = useState("");

  const [jefeObjectId, setJefeObjectId] = useState("");
  const [documentoJefe, setDocumentoJefe] = useState("");

  // 🔹 Etapa práctica
  const [estadoEtapaPractica] = useState("");
  const [fechaInicioEtapaPractica, setFechaInicioEtapaPractica] = useState("");
  const [fechaFinEtapaPractica, setFechaFinEtapaPractica] = useState("");
  const [clasificacionEtapaPractica, setClasificacionEtapaPractica] =
    useState("publica");

  // ✅ Nombres alineados al backend
  const [alternativaEtapaPractica, setAlternativaEtapaPractica] = useState("");
  const [subtipoAlternativaEtapaPractica, setSubtipoAlternativaEtapaPractica] =
    useState("");

  // 🔹 ARL como STRINGS
  const [afiliadoARL, setAfiliadoARL] = useState("");
  const [nivelRiesgoARL, setNivelRiesgoARL] = useState("");
  const [riesgoCorrespondeARL, setRiesgoCorrespondeARL] = useState("");
  const [cuentaEPP, setCuentaEPP] = useState("");

  // Auxiliares UI
  const [nombreEnte, setNombreEnte] = useState("");
  const [enteBloqueadoPorAsociacion, setEnteBloqueadoPorAsociacion] =
    useState(false);

  const [nombreJefe, setNombreJefe] = useState("");
  const [estadoAsignacion, setEstadoAsignacion] = useState("");
  const [mensajeAsociacion, setMensajeAsociacion] = useState("");
  const [telefono1Jefe, setTelefono1Jefe] = useState("");
  const [correoJefe, setCorreoJefe] = useState("");

  const [instructorNombre, setInstructorNombre] = useState("");
  const [instructorEmail, setInstructorEmail] = useState("");
  const [instructorId, setInstructorId] = useState("");

  // Modo edición
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    setEdit(!!epracticaId);
  }, [epracticaId]);

  // Prellenar ids desde la URL (CREACIÓN)
  useEffect(() => {
    if (!isBulk && aprendizId && !edit) setAprendizObjectId(aprendizId);
  }, [aprendizId, edit, isBulk]);

  useEffect(() => {
    if (matriculaId && !edit) setMatriculaObjectId(matriculaId);
  }, [matriculaId, edit]);

  const getData = useCallback(async () => {
    try {
      if (!epracticaId) return;

      const { data } = await axios.get(`/api/etapapractica/listid/${epracticaId}`);
      if (!data?.ok) return;

      const detalle = data.etapa || {};

      // ========= Aprendiz =========
      const aprendizId =
        detalle.aprendizObjectId?._id || detalle.aprendizObjectId || "";
      setAprendizObjectId(String(aprendizId));

      const docAprendiz = detalle.documentoAprendiz != null ? String(detalle.documentoAprendiz) : "";
      setDocumentoAprendiz(docAprendiz);
      setDocumentoAprendizUI(docAprendiz); // ✅ ESTE ES EL QUE PINTA EL INPUT

      // (Opcional) si muestras card de aprendizInfo / nombre completo
      if (detalle.aprendizObjectId && typeof detalle.aprendizObjectId === "object") {
        setAprendizInfo(detalle.aprendizObjectId);
      } else {
        setAprendizInfo({ documentoAprendiz: docAprendiz });
      }

      // ========= Matrícula =========
      const matId =
        detalle.matriculaObjectId?._id || detalle.matriculaObjectId || "";
      setMatriculaObjectId(String(matId));

      const codMat = detalle.codMatricula != null ? String(detalle.codMatricula) : "";
      setCodMatricula(codMat);

      // ✅ esto es lo que se ve en el input datalist
      const ficha = detalle.matriculaObjectId?.fichaGrupo ?? "N/A";
      const anio = detalle.matriculaObjectId?.anioLectivo ?? "";
      setMatriculaUI(`Matrícula ${codMat || "N/A"} — Ficha ${ficha} — ${anio}`);

      // ========= Fechas =========
      setFechaInicioEtapaPractica(
        detalle.fechaInicioEtapaPractica
          ? new Date(detalle.fechaInicioEtapaPractica).toISOString().slice(0, 10)
          : ""
      );
      setFechaFinEtapaPractica(
        detalle.fechaFinEtapaPractica
          ? new Date(detalle.fechaFinEtapaPractica).toISOString().slice(0, 10)
          : ""
      );

      // ========= Clasificación =========
      setClasificacionEtapaPractica(detalle.clasificacionEtapaPractica || "publica");

      // ========= Alternativa =========
      setAlternativaEtapaPractica(detalle.alternativaEtapaPractica || "");
      setSubtipoAlternativaEtapaPractica(detalle.subtipoAlternativaEtapaPractica || "");
      setAlternativa(detalle.alternativaEtapaPractica || "");
      setSubtipo(detalle.subtipoAlternativaEtapaPractica || "");

      // ========= Ente conformador =========
      const enteId = detalle.enteObjectId?._id || detalle.enteObjectId || "";
      setEnteObjectId(String(enteId));

      setNombreEnte(
        detalle.enteObjectId?.nombreEnteconformador || detalle.nombreEnteconformador || ""
      );

      // ⚠️ NIT grande -> siempre string en el front
      setNitEnteconformador(
        detalle.nitEnteconformador != null ? String(detalle.nitEnteconformador) : ""
      );

      // ========= Instructor =========
      const docInst = detalle.documentoInstructor != null ? String(detalle.documentoInstructor) : "";
      setDocumentoInstructor(docInst);

      const instId =
        detalle.instructorObjectId?._id ||
        detalle.instructorObjectId ||
        "";
      setInstructorId(String(instId || ""));
      setInstructorObjectId(String(instId || ""));

      // nombre completo con lo que trae el populate
      const instNombre =
        detalle.instructorObjectId
          ? [
            detalle.instructorObjectId.nombre1Instructor,
            detalle.instructorObjectId.nombre2Instructor,
            detalle.instructorObjectId.apellido1Instructor,
            detalle.instructorObjectId.apellido2Instructor,
          ].filter(Boolean).join(" ")
          : "";

      setInstructorNombre(instNombre || detalle.nombreCompleto || "");
      setInstructorEmail(detalle.instructorObjectId?.emailInstructor || detalle.emailInstructor || "");

      // ========= Jefe =========
      const docJefe = detalle.documentoJefe != null ? String(detalle.documentoJefe) : "";
      setDocumentoJefe(docJefe);

      const jefeId =
        detalle.jefeObjectId?._id || detalle.jefeObjectId || "";
      setJefeObjectId(String(jefeId || ""));

      const jefeNombre =
        detalle.jefeObjectId
          ? [
            detalle.jefeObjectId.nombre1Jefe,
            detalle.jefeObjectId.nombre2Jefe,
            detalle.jefeObjectId.apellido1Jefe,
            detalle.jefeObjectId.apellido2Jefe,
          ].filter(Boolean).join(" ")
          : "";

      setNombreJefe(jefeNombre || detalle.nombreJefe || "");

      setTelefono1Jefe(
        detalle.jefeObjectId?.telefono1Jefe || detalle.telefono1Jefe || ""
      );
      setCorreoJefe(
        detalle.jefeObjectId?.correoJefe ||
        detalle.jefeObjectId?.emailJefe ||
        detalle.correoJefe ||
        ""
      );

      // ========= Riesgos =========
      setAfiliadoARL(detalle.afiliadoARL || "");
      setNivelRiesgoARL(detalle.nivelRiesgoARL != null ? String(detalle.nivelRiesgoARL) : "");
      setRiesgoCorrespondeARL(detalle.riesgoCorrespondeARL || "");
      setCuentaEPP(detalle.cuentaEPP || "");

    } catch (error) {
      console.error("❌ Error cargando datos para editar:", error);
    }
  }, [epracticaId]);


  useEffect(() => {
    if (epracticaId) getData();
  }, [epracticaId, getData]);




  // ===== Alternativas =====
  const alternativas = useMemo(
    () => ({
      "Proyecto Productivo": [
        "Sena - Empresa",
        "Sena Proveedor Sena",
        "Producción de Centros",
        "Proyecto productivo I+D+i",
        "Proyectos Ruta Emprendedora SENA",
        "Prácticas en economía popular/campesina",
        "Convocatorias SENNOVA",
      ],
      "Monitoría": [
        "Monitoría regular",
        "Monitoría convocatorias SENNOVA",
        "Otra",
      ],
      "Contrato Vínculo Formativo": [
        "Apoyo a una unidad productiva familiar",
        "De apoyo a institución estatal/ONG/sin ánimo de lucro",
        "Asesoría a pymes",
        "Prácticas en economía popular/campesina",
      ],
      "Contrato de Aprendizaje": [
        "Contrato de aprendizaje (regular)",
        "Convocatorias SENNOVA",
        "Prácticas en economía popular/campesina",
      ],
      "Vínculo Laboral": [
        "Vínculo laboral (regular)",
        "Prácticas en economía popular/campesina",
      ],
    }),
    []
  );

  // UI selects (alternativa/subtipo)
  const [alternativa, setAlternativa] = useState("");
  const [subtipo, setSubtipo] = useState("");

  const handleAlternativaChange = (e) => {
    const v = e.target.value;
    setAlternativa(v);
    setSubtipo("");
    setAlternativaEtapaPractica(v);
    setSubtipoAlternativaEtapaPractica("");
  };

  // ===== Fechas: inicio + 6 meses (auto) =====
  const handleInicioChange = (e) => {
    const v = e.target.value;
    setFechaInicioEtapaPractica(v);
    setFechaFinEtapaPractica(v ? addMonthsClamped(v, 6) : "");
  };

  useEffect(() => {
    if (fechaInicioEtapaPractica && (!edit || !fechaFinEtapaPractica)) {
      setFechaFinEtapaPractica(addMonthsClamped(fechaInicioEtapaPractica, 6));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaInicioEtapaPractica, edit]);

  // =======================
  // ✅ Cargar matrículas por documento (individual)
  // =======================
  const cargarMatriculasPorDocumento = useCallback(async (doc) => {
    const v = String(doc ?? "").trim();
    if (!v) return;

    try {
      setLoadingMatriculas(true);

      // reset previo
      setAprendizInfo(null);
      setAprendizObjectId("");
      setMatriculaObjectId("");
      setCodMatricula("");
      setMatriculaUI("");
      setMatriculasOptions([]);

      const { data } = await axios.get(
        `/api/detallematricula/listbymatriculasdocumento/${v}`
      );

      // ✅ set aprendiz
      if (data?.aprendizId) setAprendizObjectId(String(data.aprendizId));
      setDocumentoAprendiz(v);

      // ✅ info del aprendiz
      if (data?.aprendiz) {
        setAprendizInfo(data.aprendiz);
      } else {
        setAprendizInfo({ documentoAprendiz: v });
      }

      // ✅ set matrículas
      const lista = data?.matriculas || [];
      const options = lista.map((m) => ({
        _id: m._id,
        codMatricula: m.codMatricula,
        fichaGrupo: m.fichaGrupo,
        anioLectivo: m.anioLectivo,
        label: `Matrícula ${m.codMatricula ?? "N/A"} — Ficha ${m.fichaGrupo ?? "N/A"
          } — ${m.anioLectivo ?? ""}`,
      }));

      setMatriculasOptions(options);

      // ✅ autoselección si hay una sola
      if (options.length === 1) {
        setMatriculaObjectId(options[0]._id);
        setCodMatricula(String(options[0].codMatricula ?? ""));
        setMatriculaUI(options[0].label);
      }

      if (options.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Sin matrículas",
          text: "Este aprendiz no tiene matrículas asociadas.",
        });
      }
    } catch (error) {
      setAprendizInfo(null);
      setMatriculasOptions([]);
      setAprendizObjectId("");
      setMatriculaObjectId("");
      setCodMatricula("");
      setMatriculaUI("");

      Swal.fire({
        icon: "warning",
        title: "No se pudo cargar",
        text:
          error?.response?.data?.message ||
          "Verifica el documento del aprendiz.",
      });
    } finally {
      setLoadingMatriculas(false);
    }
  }, []);


  // ✅ Si estás en individual y viene aprendizId por URL, puedes cargar matrículas por id
  const cargarMatriculasPorAprendizId = useCallback(async (aId) => {
    if (!aId) return;

    try {
      setLoadingMatriculas(true);

      const { data } = await axios.get(
        `/api/detallematricula/listbymatriculasaprendiz/${aId}`
      );

      const lista = data?.matriculas || [];
      const options = lista.map((m) => ({
        _id: m._id,
        codMatricula: m.codMatricula,
        fichaGrupo: m.fichaGrupo,
        anioLectivo: m.anioLectivo,
        label: `Matrícula ${m.codMatricula ?? "N/A"} — Ficha ${m.fichaGrupo ?? "N/A"
          } — ${m.anioLectivo ?? ""}`,
      }));

      setMatriculasOptions(options);

      // ✅ autoselección si solo hay una
      if (options.length === 1) {
        setMatriculaObjectId(options[0]._id);
        setCodMatricula(String(options[0].codMatricula ?? ""));
        setMatriculaUI(options[0].label);
      }
    } catch (error) {
      setMatriculasOptions([]);
    } finally {
      setLoadingMatriculas(false);
    }
  }, []);


  useEffect(() => {
    if (!isBulk && !edit && aprendizObjectId && !documentoAprendiz) {
      // Si tienes aprendizId por url pero no documento digitado, por lo menos carga matrículas
      cargarMatriculasPorAprendizId(aprendizObjectId);
    }
  }, [aprendizObjectId, cargarMatriculasPorAprendizId, documentoAprendiz, edit, isBulk]);

  // ✅ elegir matrícula desde input (datalist)
  const onPickMatriculaFromInput = (value) => {
    setMatriculaUI(value);
    const opt = matriculasOptions.find((m) => m.label === value);
    if (opt) {
      setMatriculaObjectId(opt._id);
      setCodMatricula(String(opt.codMatricula ?? ""));
    } else {
      setMatriculaObjectId("");
      setCodMatricula("");
    }
  };

  // =======================
  // Clean
  // =======================
  const cleanData = useCallback(() => {
    setClasificacionEtapaPractica("publica");

    setAlternativa("");
    setSubtipo("");
    setAlternativaEtapaPractica("");
    setSubtipoAlternativaEtapaPractica("");

    // ✅ en masivo oculto, en individual sí limpia estos inputs
    if (!isBulk) {
      setAprendizInfo(null);
      setDocumentoAprendizUI("");
      setMatriculaUI("");
      setMatriculasOptions([]);

      setAprendizObjectId("");
      setDocumentoAprendiz("");
      setMatriculaObjectId("");
      setCodMatricula("");
    }

    setNitEnteconformador("");
    setNombreEnte("");
    setEnteObjectId("");
    setEnteBloqueadoPorAsociacion(false);

    setDocumentoJefe("");
    setNombreJefe("");
    setEstadoAsignacion("");
    setMensajeAsociacion("");
    setTelefono1Jefe("");
    setCorreoJefe("");
    setJefeObjectId("");

    setDocumentoInstructor("");
    setInstructorNombre("");
    setInstructorEmail("");
    setInstructorId("");
    setInstructorObjectId("");

    setAfiliadoARL("");
    setNivelRiesgoARL("");
    setRiesgoCorrespondeARL("");
    setCuentaEPP("");

    setFechaInicioEtapaPractica("");
    setFechaFinEtapaPractica("");
  }, [isBulk]);

  // =======================
  // Validación & Payload
  // =======================
  const validate = useCallback(() => {
    const missing = [];

    if (!isBulk && !aprendizObjectId) missing.push("Aprendiz (por documento)");
    if (!matriculaObjectId) missing.push("Matrícula (selecciona una sugerencia)");
    if (!clasificacionEtapaPractica) missing.push("Clasificación de la Etapa");
    if (!alternativaEtapaPractica)
      missing.push("Alternativa de Etapa Productiva");
    if (!afiliadoARL) missing.push("Afiliación ARL (SI/NO)");
    if (!riesgoCorrespondeARL) missing.push("¿Riesgo corresponde? (SI/NO)");
    if (!cuentaEPP) missing.push("¿Cuenta con EPP? (SI/NO/NA)");
    if (!documentoInstructor || isNaN(Number(documentoInstructor))) {
      missing.push("Documento del Instructor válido");
    }

    if (fechaInicioEtapaPractica && fechaFinEtapaPractica) {
      const ini = new Date(`${fechaInicioEtapaPractica}T00:00:00`);
      const fin = new Date(`${fechaFinEtapaPractica}T00:00:00`);
      if (fin < ini) {
        Swal.fire({
          icon: "warning",
          title: "Rango de fechas inválido",
          text: "La fecha de fin no puede ser anterior a la fecha de inicio.",
        });
        return false;
      }
    }

    if (missing.length) {
      Swal.fire({
        icon: "warning",
        title: "Faltan datos obligatorios",
        html: `<ul class="text-start mb-0">${missing
          .map((m) => `<li>${m}</li>`)
          .join("")}</ul>`,
      });
      return false;
    }

    if (!Object.keys(alternativas).includes(alternativaEtapaPractica)) {
      Swal.fire({ icon: "warning", title: "Alternativa inválida" });
      return false;
    }

    return true;
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    alternativas,
    clasificacionEtapaPractica,
    cuentaEPP,
    documentoInstructor,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    isBulk,
    aprendizObjectId,
    matriculaObjectId,
    riesgoCorrespondeARL,
  ]);

  const toNumberOrUndef = (v) =>
    v !== "" && !Number.isNaN(Number(v)) ? Number(v) : undefined;

  const buildPayload = useCallback(() => {
    const raw = {
      aprendizObjectId,
      documentoAprendiz: toNumberOrUndef(documentoAprendiz),
      matriculaObjectId,
      codMatricula: toNumberOrUndef(codMatricula),

      enteObjectId,
      nitEnteconformador: toNumberOrUndef(nitEnteconformador),

      instructorObjectId,
      documentoInstructor: toNumberOrUndef(documentoInstructor),

      jefeObjectId,
      documentoJefe: toNumberOrUndef(documentoJefe),

      estadoEtapaPractica,

      fechaInicioEtapaPractica: toISODateUTC(fechaInicioEtapaPractica),
      fechaFinEtapaPractica: toISODateUTC(fechaFinEtapaPractica),

      clasificacionEtapaPractica,

      alternativaEtapaPractica,
      subtipoAlternativaEtapaPractica,

      afiliadoARL,
      nivelRiesgoARL: toNumberOrUndef(nivelRiesgoARL),
      riesgoCorrespondeARL,
      cuentaEPP,
    };

    return Object.fromEntries(
      Object.entries(raw).filter(
        ([_, v]) => !(typeof v === "string" && v.trim() === "")
      )
    );
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    aprendizObjectId,
    clasificacionEtapaPractica,
    codMatricula,
    cuentaEPP,
    documentoAprendiz,
    documentoInstructor,
    documentoJefe,
    enteObjectId,
    estadoEtapaPractica,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    jefeObjectId,
    matriculaObjectId,
    nitEnteconformador,
    instructorObjectId,
    nivelRiesgoARL,
    riesgoCorrespondeARL,
    subtipoAlternativaEtapaPractica,
  ]);

  const buildBulkPayload = useCallback(() => {
    const raw = {
      enteObjectId,
      nitEnteconformador: toNumberOrUndef(nitEnteconformador),

      instructorObjectId,
      documentoInstructor: toNumberOrUndef(documentoInstructor),

      jefeObjectId,
      documentoJefe: toNumberOrUndef(documentoJefe),

      estadoEtapaPractica,

      fechaInicioEtapaPractica: toISODateUTC(fechaInicioEtapaPractica),
      fechaFinEtapaPractica: toISODateUTC(fechaFinEtapaPractica),

      clasificacionEtapaPractica,
      alternativaEtapaPractica,
      subtipoAlternativaEtapaPractica,

      afiliadoARL,
      nivelRiesgoARL: toNumberOrUndef(nivelRiesgoARL),
      riesgoCorrespondeARL,
      cuentaEPP,

      soloActivos: true,
      dryRun: false,
    };

    return Object.fromEntries(
      Object.entries(raw).filter(
        ([_, v]) => !(typeof v === "string" && v.trim() === "")
      )
    );
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    clasificacionEtapaPractica,
    cuentaEPP,
    documentoInstructor,
    documentoJefe,
    enteObjectId,
    estadoEtapaPractica,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    jefeObjectId,
    nitEnteconformador,
    instructorObjectId,
    nivelRiesgoARL,
    riesgoCorrespondeARL,
    subtipoAlternativaEtapaPractica,
  ]);

  // =======================
  // API Guardar / Update
  // =======================
  const saveEPractica = useCallback(async () => {
    try {
      if (!validate()) return;

      await axios.post("/api/etapapractica/add", buildPayload());
      cleanData();

      await Swal.fire({
        icon: "success",
        title: "Etapa práctica registrada",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });

      navigate("/etapaproductiva/listareproductiva");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error?.response?.data?.message ||
          "Ya existe una etapa práctica para este aprendiz y matrícula.",
        confirmButtonText: "Volver",
      });

      navigate(-1);
    }
  }, [buildPayload, cleanData, navigate, validate]);


  const updateEPractica = useCallback(async () => {
    try {
      if (!validate()) return;

      if (!epracticaId) {
        return Swal.fire({
          icon: "warning",
          title: "ID de etapa no encontrado para actualizar",
        });
      }

      await axios.put(
        `/api/etapapractica/update/${epracticaId}`,
        buildPayload()
      );

      cleanData();

      Swal.fire({
        icon: "success",
        title: "Etapa práctica actualizada",
        showConfirmButton: false,
        timer: 1500,
      });

      navigate("/etapaproductiva/listareproductiva");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: error?.response?.data?.message || error.message,
      });
    }
  }, [buildPayload, cleanData, epracticaId, navigate, validate]);


  const saveEPracticaBulk = useCallback(async () => {
    try {
      if (!validate()) return;

      const mid = matriculaId || matriculaObjectId;
      if (!mid) {
        return Swal.fire({
          icon: "warning",
          title: "Falta el ID de matrícula",
          text: "No se encontró el parámetro en la URL.",
        });
      }

      await axios.post(
        `/api/etapapractica/bulk/${mid}`,
        buildBulkPayload()
      );

      await Swal.fire({
        icon: "success",
        title: "Registro masivo completado",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/etapaproductiva/listareproductiva");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error en el registro masivo",
        text:
          error?.response?.data?.message ||
          "No fue posible crear la etapa productiva masiva.",
      });
    }
  }, [buildBulkPayload, matriculaId, matriculaObjectId, navigate, validate]);

  const actions = useCallback(
    (e) => {
      e.preventDefault();
      if (isBulk) return saveEPracticaBulk();
      return edit ? updateEPractica() : saveEPractica();
    },
    [edit, isBulk, saveEPractica, saveEPracticaBulk, updateEPractica]
  );


  // =======================
  // Verificaciones externas
  // =======================
  const verificarNit = useCallback(async (nit) => {
    if (!nit) return;

    try {
      const response = await axios.get(`/api/ente/verificar/${nit}`);

      if (response.data?.exists) {
        setNombreEnte(response.data.nombreEnteconformador || "");
        setEnteObjectId(response.data.enteId || response.data._id || "");
        setEnteBloqueadoPorAsociacion(false);
      } else {
        setNombreEnte("");
        setEnteObjectId("");
      }
    } catch (error) {
      console.error("Error al verificar el NIT:", error);
      setNombreEnte("");
      setEnteObjectId("");
    }
  }, []);


  const verificarJefeActivo = useCallback(async (doc) => {
    if (!doc) return;

    try {
      const { data } = await axios.get(
        `/api/asociarjefes/verificar/${encodeURIComponent(doc)}/Activo`
      );

      if (data?.ok && data.exists) {
        setDocumentoJefe(String(doc));
        setNombreJefe(data.nombreJefe || "");
        setEstadoAsignacion(data.estadoAsignacion || "Activo");
        setMensajeAsociacion("Asociación activa encontrada.");

        const jefeId =
          data.jefeObjectId?._id ||
          data.jefeObjectId ||
          data.asociacion?.jefeObjectId?._id ||
          data.asociacion?.jefeObjectId ||
          data.jefeId ||
          "";
        setJefeObjectId(jefeId || "");

        const tel =
          data.telefono1Jefe ||
          data.asociacion?.jefeObjectId?.telefono1Jefe ||
          "";
        const mail =
          data.correoJefe ||
          data.asociacion?.jefeObjectId?.correoJefe ||
          data.asociacion?.jefeObjectId?.emailJefe ||
          "";
        setTelefono1Jefe(tel);
        setCorreoJefe(mail);

        const enteNombre =
          data.enteConformador ||
          data.asociacion?.enteObjectId?.nombreEnteconformador ||
          "";
        const enteNit =
          data.asociacion?.enteObjectId?.nitEnteconformador || "";
        const enteId =
          data.asociacion?.enteObjectId?._id ||
          data.asociacion?.enteObjectId ||
          "";

        if (enteNombre) setNombreEnte(enteNombre);
        if (enteNit) setNitEnteconformador(String(enteNit));
        if (enteId) setEnteObjectId(String(enteId));

        if (enteNombre || enteNit) setEnteBloqueadoPorAsociacion(true);
      } else {
        setNombreJefe("");
        setEstadoAsignacion("");
        setMensajeAsociacion(
          data?.message || "El jefe no tiene asociación activa."
        );
        setTelefono1Jefe("");
        setCorreoJefe("");
        setJefeObjectId("");
        setEnteBloqueadoPorAsociacion(false);
      }
    } catch (error) {
      console.error("Error al verificar la asociación activa:", error);
      setNombreJefe("");
      setEstadoAsignacion("");
      setMensajeAsociacion("Error al verificar la asociación activa.");
      setTelefono1Jefe("");
      setCorreoJefe("");
      setJefeObjectId("");
      setEnteBloqueadoPorAsociacion(false);
    }
  }, []);



  const verificarInstructor = useCallback(async (doc) => {
    if (!doc) return;

    try {
      const { data } = await axios.get(
        `/api/instructores/verificar/${encodeURIComponent(doc)}`
      );

      if (data?.exists) {
        setDocumentoInstructor(String(doc));
        setInstructorNombre(data.nombreCompleto || "");
        setInstructorEmail(data.emailInstructor || "");

        const instId =
          data.instructorObjectId?._id ||
          data.instructorObjectId ||
          data.instructor?._id ||
          data.instructorId ||
          "";

        setInstructorId(instId || "");
        setInstructorObjectId(instId || "");
      } else {
        setInstructorNombre("");
        setInstructorEmail("");
        setInstructorId("");
        setInstructorObjectId("");
      }
    } catch (error) {
      console.error("Error al verificar el documento del instructor:", error);
      setInstructorNombre("");
      setInstructorEmail("");
      setInstructorId("");
      setInstructorObjectId("");
    }
  }, []);


  const handleDocInstructorChange = (e) => setDocumentoInstructor(e.target.value);

  // =======================
  // UI helpers
  // =======================
  const titulo = isBulk
    ? "Registrar Etapa Práctica (Masivo por Matrícula)"
    : edit
      ? "Editar Etapa Práctica"
      : "Registrar Etapa Práctica";



  const nombreCompletoAprendiz = useMemo(() => {
    if (!aprendizInfo) return "";
    return [
      aprendizInfo.nombre1Aprendiz,
      aprendizInfo.nombre2Aprendiz,
      aprendizInfo.apellido1Aprendiz,
      aprendizInfo.apellido2Aprendiz,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [aprendizInfo]);

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* ===== Header ===== */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h5 className="mt-2 mb-1 fw-semibold">{titulo}</h5>
                </div>
                <small className="text-secondary">
                  Diligencia la información por pestañas. El período se calcula automáticamente (+6 meses).
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => navigate(-1)}
                  title="Cancelar"
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={actions}>
            <div className="p-3 p-md-4">
              {/* ✅ SOLO INDIVIDUAL: Inputs documento + matrícula */}
              {!isBulk && (
                <div className="border rounded-4 p-3 mb-3 bg-body-tertiary">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    {/* Título */}
                    <div className="fw-semibold">
                      <i className="fa-solid fa-user-graduate me-2"></i>
                      Aprendiz y Matrícula
                    </div>

                    {/* Datos aprendiz */}
                    {aprendizInfo && (
                      <div className="d-flex align-items-center flex-wrap gap-4 small">
                        <div className="d-flex align-items-center gap-2">
                          <i className="fa-solid fa-user text-secondary"></i>
                          <span className="fw-semibold">
                            {nombreCompletoAprendiz || "—"}
                          </span>
                        </div>

                        <div className="d-flex align-items-center gap-2 text-muted">
                          <i className="fa-solid fa-id-card"></i>
                          <span>
                            Doc: <strong>{aprendizInfo.documentoAprendiz ?? documentoAprendiz ?? "—"}</strong>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>


                  <div className="row g-3 mt-2">
                    {/* Documento aprendiz */}
                    <div className="col-12 col-lg-6">
                      <div className="border rounded-4 p-3 bg-white h-100">
                        <label className="form-label small fw-semibold">
                          Documento del Aprendiz
                        </label>

                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="fa-solid fa-hashtag"></i>
                          </span>

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Digite documento del aprendiz"
                            value={documentoAprendizUI}
                            onChange={(e) => setDocumentoAprendizUI(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); cargarMatriculasPorDocumento(documentoAprendizUI); } }}
                            onBlur={() => { if (documentoAprendizUI?.trim()) { cargarMatriculasPorDocumento(documentoAprendizUI); } }}
                          />

                          <button
                            type="button"
                            className="btn btn-dark"
                            onClick={() => cargarMatriculasPorDocumento(documentoAprendizUI)}
                            title="Buscar">
                            <i className={`fa-solid ${loadingMatriculas ? "fa-spinner fa-spin" : "fa-magnifying-glass"}`}></i>
                          </button>
                        </div>



                        {!aprendizInfo && (
                          <div className="form-text">
                            Escribe el <strong> Documento</strong> para cargar la matrículas.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Matrícula input con sugerencias */}
                    <div className="col-12 col-lg-6">
                      <div className="border rounded-4 p-3 bg-white h-100">
                        <label className="form-label small fw-semibold">
                          Matrícula
                        </label>

                        <input
                          className="form-control"
                          list="matriculasDatalist"
                          placeholder={"Empieza a escribir y selecciona una matrícula..."}
                          value={matriculaUI}
                          onChange={(e) => onPickMatriculaFromInput(e.target.value)}
                          disabled={loadingMatriculas || matriculasOptions.length === 0}
                        />

                        <div className="form-text">
                          Para guardar es obligatorio la <strong>matricula</strong>.
                        </div>

                        {matriculaUI && !matriculaObjectId && (
                          <div className="alert alert-warning py-2 mt-2 mb-0">
                            <i className="fa-solid fa-triangle-exclamation me-2"></i>
                            Debes seleccionar una matrícula válida desde las sugerencias.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Clasificación */}
              <div className="border rounded-4 p-3 mb-3 bg-body-tertiary">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="fw-semibold">
                    <i className="fa-solid fa-shield-halved me-2"></i>
                    Clasificación del documento
                  </div>
                  <span className="small text-secondary">
                    Selección actual: <strong>{clasificacionEtapaPractica}</strong>
                  </span>
                </div>

                <div className="btn-group w-100 mt-3" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="clasificacion"
                    id="opt-publica"
                    autoComplete="off"
                    value="publica"
                    checked={clasificacionEtapaPractica === "publica"}
                    onChange={(e) => setClasificacionEtapaPractica(e.target.value)}
                  />

                  <label className="btn btn-outline-success" htmlFor="opt-publica">
                    Pública
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="clasificacion"
                    id="opt-clasificada"
                    autoComplete="off"
                    value="clasificada"
                    checked={clasificacionEtapaPractica === "clasificada"}
                    onChange={(e) => setClasificacionEtapaPractica(e.target.value)}
                  />

                  <label className="btn btn-outline-primary" htmlFor="opt-clasificada">
                    Pública Clasificada
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="clasificacion"
                    id="opt-reservada"
                    autoComplete="off"
                    value="reservada"
                    checked={clasificacionEtapaPractica === "reservada"}
                    onChange={(e) => setClasificacionEtapaPractica(e.target.value)}
                  />

                  <label className="btn btn-outline-warning" htmlFor="opt-reservada">
                    Pública Reservada
                  </label>
                </div>
              </div>

              {/* Periodo */}
              <div className="border rounded-4 p-3 mb-3">
                <div className="fw-semibold mb-2">
                  <i className="fa-solid fa-calendar-days me-2"></i>
                  Período de etapa práctica
                </div>

                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-3">
                    <label className="form-label small">Fecha Inicio</label>

                    <input
                      type="date"
                      className="form-control"
                      value={fechaInicioEtapaPractica}
                      onChange={handleInicioChange}
                    />
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label small">
                      Fecha Fin (auto +6 meses)
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={fechaFinEtapaPractica}
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <ul className="nav nav-tabs mt-2" id="myTab" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="EnteCoformador-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#EnteCoformador"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-building me-2"></i>Ente / Jefe
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="InstructorAsignado-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#InstructorAsignado"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-user-tie me-2"></i>Instructor
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="Alternativa-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#Alternativa"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-list-check me-2"></i>Alternativa
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="RiesgosLaborales-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#RiesgosLaborales"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-helmet-safety me-2"></i>Riesgos
                  </button>
                </li>
              </ul>

              <div className="tab-content p-3 border border-top-0 bg-white rounded-bottom-4" id="myTabContent">
                {/* TAB 1 */}
                <div className="tab-pane fade show active" id="EnteCoformador" role="tabpanel" >
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label small">Documento Jefe</label>
                      <input
                        type="text"
                        className="form-control"
                        value={documentoJefe}
                        onChange={(e) => setDocumentoJefe(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab")
                            verificarJefeActivo(e.target.value);
                        }}
                        onBlur={(e) => verificarJefeActivo(e.target.value)}
                        placeholder="Ej: 12345678"
                      />
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small">Nombre del Jefe</label>
                      <input
                        type="text"
                        className="form-control"
                        value={nombreJefe}
                        onChange={(e) => setNombreJefe(e.target.value)}
                        placeholder="Se autollenará si tiene asociación activa"
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">Teléfono Jefe</label>
                      <input
                        type="text"
                        className="form-control"
                        value={telefono1Jefe}
                        onChange={(e) => setTelefono1Jefe(e.target.value)}
                        placeholder="Teléfono"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label small">Email Jefe</label>
                      <input
                        type="email"
                        className="form-control"
                        value={correoJefe}
                        onChange={(e) => setCorreoJefe(e.target.value)}
                        placeholder="correo@dominio.com"
                      />
                    </div>

                    <div className="col-12">
                      {mensajeAsociacion && (
                        <div
                          className={`alert ${estadoAsignacion === "Activo"
                            ? "alert-success"
                            : "alert-warning"
                            } py-2 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2`}
                        >
                          <div>
                            <i className="fa-solid fa-circle-info me-2"></i>
                            <strong>{mensajeAsociacion}</strong>
                          </div>
                          {estadoAsignacion && (
                            <span className="badge bg-dark">
                              Estado: {estadoAsignacion}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">
                        Nit Empresa{" "}
                        {enteBloqueadoPorAsociacion && (
                          <span className="badge bg-secondary ms-1">
                            Bloqueado
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={nitEnteconformador}
                        onChange={(e) => setNitEnteconformador(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab")
                            verificarNit(e.target.value);
                        }}
                        onBlur={(e) => verificarNit(e.target.value)}
                        placeholder="Ej: 900123456"
                        disabled={enteBloqueadoPorAsociacion}
                      />
                    </div>

                    <div className="col-md-10">
                      <label className="form-label small">
                        Nombre del Ente{" "}
                        {enteBloqueadoPorAsociacion && (
                          <span className="badge bg-secondary ms-1">
                            Bloqueado
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={nombreEnte}
                        onChange={(e) => setNombreEnte(e.target.value)}
                        disabled={enteBloqueadoPorAsociacion}
                      />
                      <input type="hidden" value={enteObjectId} readOnly />

                      {enteBloqueadoPorAsociacion && (
                        <div className="form-text">
                          El Ente proviene de la asociación activa del jefe. Para editarlo, cambia/elimina el jefe y vuelve a verificar.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* TAB 2 */}
                <div className="tab-pane fade" id="InstructorAsignado" role="tabpanel" >
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label small">
                        Documento Instructor
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={documentoInstructor}
                        onChange={handleDocInstructorChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab")
                            verificarInstructor(e.target.value);
                        }}
                        onBlur={(e) => verificarInstructor(e.target.value)}
                        placeholder="Ej: 1077866875"
                      />
                      <input type="hidden" value={instructorId} readOnly />
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small">Nombre Instructor</label>
                      <input
                        type="text"
                        className="form-control"
                        value={instructorNombre}
                        onChange={(e) => setInstructorNombre(e.target.value)}
                        placeholder="Nombre del instructor"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">Email Instructor</label>
                      <input
                        type="email"
                        className="form-control"
                        value={instructorEmail}
                        onChange={(e) => setInstructorEmail(e.target.value)}
                        placeholder="correo@dominio.com"
                      />
                    </div>

                    <div className="col-12">
                      <div className="alert alert-light border py-2 mb-0">
                        <i className="fa-solid fa-circle-question me-2"></i>
                        Tip: escribe el documento y presiona <strong>TAB</strong> para autocompletar.
                      </div>
                    </div>
                  </div>
                </div>

                {/* TAB 3 */}
                <div className="tab-pane fade" id="Alternativa" role="tabpanel">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small">
                        Alternativa de Etapa Productiva
                      </label>
                      <select
                        className="form-select"
                        value={alternativa}
                        onChange={handleAlternativaChange}
                      >
                        <option value="">-- Seleccione --</option>
                        {Object.keys(alternativas).map((alt) => (
                          <option key={alt} value={alt}>
                            {alt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small">
                        Subtipo de Alternativa
                      </label>
                      <select
                        className="form-select"
                        value={subtipo}
                        onChange={(e) => {
                          setSubtipo(e.target.value);
                          setSubtipoAlternativaEtapaPractica(e.target.value);
                        }}
                        disabled={!alternativa}
                      >
                        <option value="">-- Seleccione --</option>
                        {alternativa &&
                          alternativas[alternativa].map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                      </select>
                      {!alternativa && (
                        <div className="form-text">
                          Primero selecciona una alternativa.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* TAB 4 */}
                <div className="tab-pane fade" id="RiesgosLaborales" role="tabpanel" >
                  <div className="card border-0">
                    <div className="card-header bg-dark text-white text-center fw-semibold rounded-3">
                      Decreto 055 de 2015 - Riesgos Laborales
                    </div>

                    <div className="card-body px-0 pt-3">
                      <div className="alert alert-secondary text-center py-2 mb-3">
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        Este espacio debe ser siempre diligenciado.
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Afiliado a ARL?
                          </label>
                          <select
                            className="form-select"
                            value={afiliadoARL}
                            onChange={(e) => setAfiliadoARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            Nivel de riesgo
                          </label>
                          <select
                            className="form-select"
                            value={nivelRiesgoARL}
                            onChange={(e) => setNivelRiesgoARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="1">Nivel 1</option>
                            <option value="2">Nivel 2</option>
                            <option value="3">Nivel 3</option>
                            <option value="4">Nivel 4</option>
                            <option value="5">Nivel 5</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Riesgo corresponde?
                          </label>
                          <select
                            className="form-select"
                            value={riesgoCorrespondeARL}
                            onChange={(e) => setRiesgoCorrespondeARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Cuenta con EPP?
                          </label>
                          <select
                            className="form-select"
                            value={cuentaEPP}
                            onChange={(e) => setCuentaEPP(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                            <option value="NA">NA</option>
                          </select>
                        </div>
                      </div>

                      <div className="alert alert-light border mt-3 mb-0">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        Recuerda: si el aprendiz no requiere EPP, selecciona{" "}
                        <strong>NA</strong>.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer sticky */}
              <div className="border-top bg-white px-3 py-3 d-flex justify-content-end gap-2 sticky-bottom">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => navigate(-1)}
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>

                <button type="submit" className="btn btn-dark">
                  <i className="fa-solid fa-floppy-disk me-2"></i>
                  {isBulk ? "Guardar Masivo" : edit ? "Actualizar" : "Guardar"}
                </button>
              </div>


            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


export default RegistrarEProductiva;
